import React, { useState, useEffect } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import Header from './Header';
import PermissionGuard from './PermissionGuard';
import ActiveOrders from './ActiveOrders';
import RecentActivity from './RecentActivity';
import { useAuth } from '../contexts/AuthContext';
import { getAllOrders } from '../lib/orderService';

const Dashboard = () => {
  const { userRole, hasPermission } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('Mes');
  const [orderTypeFilter, setOrderTypeFilter] = useState('Todas las órdenes');
  const [showTimePeriodDropdown, setShowTimePeriodDropdown] = useState(false);
  const [showOrderTypeDropdown, setShowOrderTypeDropdown] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const ordersList = await getAllOrders(forceRefresh);
      setOrders(ordersList);
    } catch (error) {
      console.error('Error fetching orders for statistics:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };


  const getFilteredOrders = () => {
    let filtered = orders;
    
    // Filter by order type
    if (orderTypeFilter !== 'Todas las órdenes') {
      filtered = filtered.filter(order => order.tipo_de_orden === orderTypeFilter);
    }
    
    return filtered;
  };

  const getDateRange = (period, baseDate = new Date()) => {
    const now = new Date(baseDate);
    
    switch (period) {
      case 'Semana':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        
        return { start: startOfWeek, end: endOfWeek };
        
      case 'Año':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        return { start: startOfYear, end: endOfYear };
        
      case 'Mes':
      default:
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start: startOfMonth, end: endOfMonth };
    }
  };

  const getOrdersInPeriod = (ordersArray, startDate, endDate) => {
    return ordersArray.filter(order => {
      const orderDate = order.fecha?.toDate ? order.fecha.toDate() : new Date(order.fecha);
      return orderDate >= startDate && orderDate <= endDate;
    });
  };

  const calculateStatistics = () => {
    const filteredOrders = getFilteredOrders();
    const now = new Date();
    
    // Current period
    const currentRange = getDateRange(timePeriod, now);
    const currentOrders = getOrdersInPeriod(filteredOrders, currentRange.start, currentRange.end);
    
    // Previous period
    let previousDate;
    switch (timePeriod) {
      case 'Semana':
        previousDate = new Date(now);
        previousDate.setDate(now.getDate() - 7);
        break;
      case 'Año':
        previousDate = new Date(now);
        previousDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'Mes':
      default:
        previousDate = new Date(now);
        previousDate.setMonth(now.getMonth() - 1);
        break;
    }
    
    const previousRange = getDateRange(timePeriod, previousDate);
    const previousOrders = getOrdersInPeriod(filteredOrders, previousRange.start, previousRange.end);
    
    // Calculate average (last 12 periods, only counting periods with orders)
    let totalForAverage = 0;
    let periodsWithOrders = 0;
    
    for (let i = 0; i < 12; i++) {
      let periodDate;
      switch (timePeriod) {
        case 'Semana':
          periodDate = new Date(now);
          periodDate.setDate(now.getDate() - (i * 7));
          break;
        case 'Año':
          periodDate = new Date(now);
          periodDate.setFullYear(now.getFullYear() - i);
          break;
        case 'Mes':
        default:
          periodDate = new Date(now);
          periodDate.setMonth(now.getMonth() - i);
          break;
      }
      
      const periodRange = getDateRange(timePeriod, periodDate);
      const periodOrders = getOrdersInPeriod(filteredOrders, periodRange.start, periodRange.end);
      
      // Only count periods that have at least one order
      if (periodOrders.length > 0) {
        totalForAverage += periodOrders.length;
        periodsWithOrders++;
      }
    }
    
    const average = periodsWithOrders > 0 ? Math.round(totalForAverage / periodsWithOrders) : 0;
    
    return {
      current: currentOrders.length,
      previous: previousOrders.length,
      average
    };
  };

  const getPeriodLabel = (period, isCurrent = true) => {
    const now = new Date();
    const date = isCurrent ? now : (() => {
      const prev = new Date(now);
      switch (period) {
        case 'Semana':
          prev.setDate(now.getDate() - 7);
          return prev;
        case 'Año':
          prev.setFullYear(now.getFullYear() - 1);
          return prev;
        case 'Mes':
        default:
          prev.setMonth(now.getMonth() - 1);
          return prev;
      }
    })();
    
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    
    switch (period) {
      case 'Semana':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        if (isCurrent) {
          return `Esta semana`;
        } else {
          return `Semana pasada`;
        }
      case 'Año':
        if (isCurrent) {
          return `Este año (${date.getFullYear()})`;
        } else {
          return `Año pasado (${date.getFullYear()})`;
        }
      case 'Mes':
      default:
        if (isCurrent) {
          return `Este mes (${monthNames[date.getMonth()]})`;
        } else {
          return `Mes pasado (${monthNames[date.getMonth()]})`;
        }
    }
  };

  const statistics = calculateStatistics();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Statistics Section */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Estadísticas</h2>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Time Period Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowTimePeriodDropdown(!showTimePeriodDropdown)}
                    className="flex items-center space-x-1 px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 text-gray-700 dark:text-gray-300"
                  >
                    <span>{timePeriod}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  
                  {showTimePeriodDropdown && (
                    <div className="absolute right-0 mt-1 w-28 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                      <div className="py-1">
                        {['Semana', 'Mes', 'Año'].map((period) => (
                          <button
                            key={period}
                            onClick={() => {
                              setTimePeriod(period);
                              setShowTimePeriodDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              timePeriod === period ? 'bg-purple-50 dark:bg-purple-900 text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Order Type Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowOrderTypeDropdown(!showOrderTypeDropdown)}
                    className="flex items-center space-x-1 px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 text-gray-700 dark:text-gray-300"
                  >
                    <span className="max-w-24 truncate">{orderTypeFilter}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  
                  {showOrderTypeDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                      <div className="py-1">
                        {['Todas las órdenes', 'Reparación', 'Ensamble', 'Garantía', 'Fabricación'].map((type) => (
                          <button
                            key={type}
                            onClick={() => {
                              setOrderTypeFilter(type);
                              setShowOrderTypeDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              orderTypeFilter === type ? 'bg-purple-50 dark:bg-purple-900 text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            {loading ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                Cargando estadísticas...
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-3 gap-2 sm:gap-3">
                {/* Current Period */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 sm:mb-0 text-center sm:text-left">
                    {getPeriodLabel(timePeriod, true)}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-1 text-center sm:text-right">
                    <span className="text-base sm:text-lg font-bold text-blue-900 dark:text-blue-100">
                      {statistics.current}
                    </span>
                    <span className="text-xs text-blue-700 dark:text-blue-300">
                      {statistics.current === 1 ? 'orden' : 'órdenes'}
                    </span>
                  </div>
                </div>
                
                {/* Previous Period */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 sm:mb-0 text-center sm:text-left">
                    {getPeriodLabel(timePeriod, false)}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-1 text-center sm:text-right">
                    <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                      {statistics.previous}
                    </span>
                    <span className="text-xs text-gray-700 dark:text-gray-300">
                      {statistics.previous === 1 ? 'orden' : 'órdenes'}
                    </span>
                  </div>
                </div>
                
                {/* Average */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-2 sm:mb-0 text-center sm:text-left">
                    {timePeriod === 'Mes' ? 'Promedio mensual' : 
                     timePeriod === 'Semana' ? 'Promedio semanal' : 
                     'Promedio anual'}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-1 text-center sm:text-right">
                    <span className="text-base sm:text-lg font-bold text-purple-900 dark:text-purple-100">
                      {statistics.average}
                    </span>
                    <span className="text-xs text-purple-700 dark:text-purple-300">
                      {statistics.average === 1 ? 'orden' : 'órdenes'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <ActiveOrders />

        <RecentActivity />
      </div>
      
      {/* Click outside to close dropdowns */}
      {(showTimePeriodDropdown || showOrderTypeDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowTimePeriodDropdown(false);
            setShowOrderTypeDropdown(false);
          }}
        ></div>
      )}
    </div>
  );
};

export default Dashboard;